import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/state/SessionProvider";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { z } from "zod";

// Security: Input validation schema
const contactSchema = z.object({
  name: z.string().trim().min(2, "Navn skal være mindst 2 tegn").max(100, "Navn må ikke overstige 100 tegn"),
  email: z.string().trim().email("Ugyldig email-adresse").max(255, "Email må ikke overstige 255 tegn"),
  subject: z.string().trim().min(3, "Emne skal være mindst 3 tegn").max(200, "Emne må ikke overstige 200 tegn"),
  message: z.string().trim().min(10, "Besked skal være mindst 10 tegn").max(5000, "Besked må ikke overstige 5000 tegn"),
});

export default function Contact() {
  const { user } = useSession();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Security: Validate input before sending to database
      const validated = contactSchema.parse(formData);

      const { error } = await supabase.from("contact_messages").insert({
        user_id: user?.id || null,
        name: validated.name,
        email: validated.email,
        subject: validated.subject,
        message: validated.message,
      });

      if (error) throw error;

      toast({
        title: "Besked sendt",
        description: "Vi vender tilbage til dig hurtigst muligt",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast({
          variant: "destructive",
          title: "Valideringsfejl",
          description: error.errors[0].message,
        });
      } else {
        console.error("Error sending message:", error);
        toast({
          variant: "destructive",
          title: "Fejl",
          description: "Kunne ikke sende besked. Prøv igen senere.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-12 max-w-2xl">
        <h1 className="text-4xl font-bold mb-4">Kontakt os</h1>
        <p className="text-muted-foreground mb-8">
          Har du spørgsmål eller brug for hjælp? Udfyld formularen nedenfor, så vender vi tilbage til dig hurtigst muligt.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="name">Navn *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Dit fulde navn"
              required
            />
          </div>

          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="din@email.dk"
              required
            />
          </div>

          <div>
            <Label htmlFor="subject">Emne *</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Hvad drejer din henvendelse sig om?"
              required
            />
          </div>

          <div>
            <Label htmlFor="message">Besked *</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Beskriv dit spørgsmål eller problem"
              rows={8}
              required
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send besked
          </Button>
        </form>

        <div className="mt-12 pt-8 border-t">
          <h2 className="text-xl font-semibold mb-4">Andre kontaktmuligheder</h2>
          <div className="space-y-2 text-muted-foreground">
            <p>Email: askyum@hotmail.com</p>
            <p>Telefon: [Indsæt telefonnummer]</p>
            <p>
              Åbningstider: Mandag-fredag 9:00-17:00
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
